// ─── Weight display units ────────────────────────────────────────────────────
// Weight is stored canonically as `Patient.weightKg` (kilograms) so all math
// (e.g. TDEE, BMI) reads a single source of truth. This module is the only
// place weight is converted to/from the user's chosen display unit, mirroring
// the fluid-unit and height-unit patterns so the API and SPA agree.

/** Display/entry unit for body weight. The stored value stays canonical in
 *  `weightKg`; this just selects how it is shown and entered. Default "lbs". */
export const WEIGHT_DISPLAY_UNITS = ["lbs", "kg"] as const;
export type WeightDisplayUnit = (typeof WEIGHT_DISPLAY_UNITS)[number];

export const WEIGHT_UNIT_LABELS: Record<WeightDisplayUnit, string> = {
  lbs: "Pounds (lbs)",
  kg: "Kilograms (kg)",
};

/** Default when the patient hasn't chosen a unit (US household default). */
export const DEFAULT_WEIGHT_UNIT: WeightDisplayUnit = "lbs";

const KG_PER_LB = 0.45359237;

/** Convert kilograms to pounds, rounded to one decimal place. */
export function kgToLbs(kg: number): number {
  return Math.round((kg / KG_PER_LB) * 10) / 10;
}

/** Convert pounds to canonical kilograms, rounded to two decimal places. */
export function lbsToKg(lbs: number): number {
  return Math.round(lbs * KG_PER_LB * 100) / 100;
}

/** Convert a canonical kg weight to the patient's preferred display value. */
export function kgToDisplay(kg: number, unit: WeightDisplayUnit): number {
  return unit === "lbs" ? kgToLbs(kg) : Math.round(kg * 10) / 10;
}

/** Convert a display value back to canonical kilograms. */
export function displayToKg(value: number, unit: WeightDisplayUnit): number {
  return unit === "lbs" ? lbsToKg(value) : Math.round(value * 100) / 100;
}

/** Format a canonical kg weight in the patient's preferred unit, e.g.
 *  "165.2 lbs" or "74.9 kg". */
export function formatWeight(kg: number, unit: WeightDisplayUnit): string {
  const v = kgToDisplay(kg, unit);
  return `${v} ${unit}`;
}
